import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout/MainLayout";
import styles from "./AuditDetails.module.scss";
import Button from "../../components/Button/Button";
import Textarea from "../../components/Textarea/Textarea";
import RaciAssignment from "../../components/RaciAssignment/RaciAssignment";
import PageLoader from "../../components/Loader/PageLoader";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Clock,
  FileText,
  MessageSquare,
  Paperclip,
  Save,
  Send,
  User,
} from "lucide-react";
import classNames from "classnames";
import { Field, Form, Formik } from "formik";
import OutsideClickHandler from "react-outside-click-handler";
import axiosInstance from "../../utils/utils";
import { getOrganizationData } from "../../actions/organization";
import DatePickerField from "../../components/FormikComponents/DatePickerField";
import useFetchOrganizationData from "../../actions/useFetchOrganizationData";
import { handleExport } from "../../utils/helpers";
import QuestionAnswerList from "../../components/QuestionAnswerList/QuestionAnswerList";
import Select from "../../components/Select/Select";
import IconSelect from "../../components/IconSelect/IconSelect";
// import WYSIWYGEditor from "../../components/WYSIWYGEditor/WYSIWYGEditor";
import ClosedLockIcon from "../../icons/lock.svg";
import PublicIcon from "../../icons/public.svg";
import { showModal } from "../../redux/modalStore";
import HsCodesPopup from "../../modals/SmallConfirmationPopup/HsCodesPopup";
import { clearHsCodes, setHsRootLoading } from "../../redux/hsCodesStore";
import { fetchHsChildren, fetchHsRoot } from "../../actions/hsCodes";
import FormikUpload from "../../components/FormikComponents/FormikUpload/FormikUpload";

const statusOptions = [
  { id: "Open", label: "Open", tone: "open" },
  { id: "In Progress", label: "In Progress", tone: "progress" },
  { id: "In Review", label: "In Review", tone: "review" },
  { id: "Done", label: "Done", tone: "done" },
];

const typeOptions = [
  { id: "Internal", label: "Internal" },
  { id: "External", label: "External" },
];

const privacyOptions = [
  {
    label: "Private Audit Task",
    value: true,
    icon: ClosedLockIcon,
  },
  {
    label: "Public Audit Task",
    value: false,
    icon: PublicIcon,
  },
];

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const getRelativeTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.round(diffMs / 1000);
  if (diffSec < 45) return "just now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
  const diffHours = Math.round(diffMin / 60);
  if (diffHours < 24) {
    return `about ${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  }
  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  const diffWeeks = Math.round(diffDays / 7);
  if (diffWeeks < 5) return `${diffWeeks} week${diffWeeks === 1 ? "" : "s"} ago`;
  const diffMonths = Math.round(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths} month${diffMonths === 1 ? "" : "s"} ago`;
  const diffYears = Math.round(diffDays / 365);
  return `${diffYears} year${diffYears === 1 ? "" : "s"} ago`;
};

const parseUpdateBody = (body) => {
  if (!body) return [];
  const rawText = typeof body === "string" ? body : String(body);
  const lines = rawText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => (line.startsWith("- ") ? line.slice(2).trim() : line));

  return lines.map((line) => {
    const colonIndex = line.indexOf(":");
    if (colonIndex > 0) {
      const label = line.slice(0, colonIndex).trim();
      const value = line.slice(colonIndex + 1).trim();
      if (label) {
        return { label, value };
      }
    }
    return { text: line };
  });
};

const getUpdateTimestamp = (update) =>
  update?.date ||
  update?.updated_at ||
  update?.updatedAt ||
  update?.updated_on ||
  update?.updatedOn ||
  update?.created_at ||
  update?.createdAt ||
  update?.created_on ||
  update?.createdOn;

const getUpdateBodyText = (update) => {
  if (!update?.body) return "";
  return typeof update.body === "string" ? update.body : String(update.body);
};

const getUpdateAuthor = (update) => {
  const candidate =
    update?.author || update?.user || update?.created_by || update?.createdBy;
  if (typeof candidate === "string") return candidate;
  if (candidate && typeof candidate === "object") {
    return candidate.email || candidate.name || "";
  }
  return "";
};

const normalizeUpdateAttachments = (attachments = []) => {
  const items = Array.isArray(attachments) ? attachments : [attachments];
  return items
    .map((attachment) => {
      if (!attachment) return null;
      if (typeof attachment === "string") {
        return {
          url: attachment,
          filename: attachment.split("/").pop() || "Attachment",
        };
      }
      const url = attachment.url || attachment.path || attachment.link || "";
      const filename =
        attachment.filename ||
        attachment.name ||
        (url ? url.split("/").pop() : "") ||
        "Attachment";
      return { url, filename };
    })
    .filter((attachment) => attachment && attachment.url);
};

const isStructuredUpdate = (update) => {
  const body = getUpdateBodyText(update).trim();
  if (!body) return false;
  return parseUpdateBody(body).some((item) => item.label);
};

const isCommentUpdate = (update) => {
  const body = getUpdateBodyText(update).trim();
  if (body) return !isStructuredUpdate(update);
  return normalizeUpdateAttachments(update?.attachments || []).length > 0;
};

const FormStateBridge = ({ dirty, isSubmitting, onChange }) => {
  useEffect(() => {
    onChange(dirty, isSubmitting);
  }, [dirty, isSubmitting, onChange]);

  return null;
};

const AuditDetails = () => {
  const { orgId, auditKey } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formikRef = useRef(null);
  const location = useLocation();

  const { auditsTasksData, findingsData } = useSelector(
    (state) => state.organization,
  );
  const userEmail = useSelector((state) => state.user.email);
  const { root, expandedNodes, rootLoading } = useSelector(
    (state) => state.hsCodes,
  );

  useFetchOrganizationData();

  const audit = useMemo(
    () =>
      Object.values(auditsTasksData || {}).find((item) => item.key === auditKey),
    [auditKey, auditsTasksData],
  );

  const hasLoadedAudits = Object.keys(auditsTasksData || {}).length > 0;

  const [activeTab, setActiveTab] = useState("details");
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [addingQuestionId, setAddingQuestionId] = useState(null);
  const [editableData, setEditableData] = useState(audit || {});
  const [hsPopupOpen, setHsPopupOpen] = useState(false);
  const [updates, setUpdates] = useState([]);
  const sessionToken = localStorage.getItem("session_token");

  useEffect(() => {
    setEditableData(audit || {});
  }, [audit]);

  useEffect(() => {
    const nextUpdates = Array.isArray(audit?.updates) ? audit.updates : [];
    setUpdates(nextUpdates);
  }, [audit?.updates]);

  const handleFormState = useCallback((dirty, submitting) => {
    setIsDirty(dirty);
    setIsSubmitting(submitting);
  }, []);

  const linkedFindingEntries = useMemo(() => {
    const linked = audit?.linked_findings || [];
    return linked
      .map((item) => {
        const key =
          typeof item === "string" || typeof item === "number" ? item : item?.key;
        const data =
          (typeof item === "object" && item) ||
          findingsData?.[key] ||
          findingsData?.[item];
        return { key, data };
      })
      .filter((entry) => entry.key);
  }, [audit, findingsData]);

  const lastUpdatedAt = updates.reduce((latest, update) => {
    const candidate = getUpdateTimestamp(update);
    if (!candidate) return latest;
    if (!latest) return candidate;
    const candidateTime = new Date(candidate).getTime();
    const latestTime = new Date(latest).getTime();
    if (Number.isNaN(candidateTime)) return latest;
    if (Number.isNaN(latestTime) || candidateTime > latestTime) return candidate;
    return latest;
  }, "");
  const showMetaCard = Boolean(lastUpdatedAt);

  const initialValues = useMemo(() => {
    const normalizedType = (() => {
      const rawType = editableData?.type || "";
      if (rawType.toLowerCase?.() === "external") return "External";
      if (rawType.toLowerCase?.() === "internal") return "Internal";
      return rawType || "Internal";
    })();

    return {
      name: editableData?.name || "",
      responses: (editableData?.form || []).reduce((acc, formItem) => {
        Object.keys(formItem.questions || {}).forEach((questionId) => {
          const question = formItem.questions[questionId];
          if (!question) return;

          if (question.type === "text") {
            acc[questionId] = question.answer || "";
          } else if (question.type === "select") {
            acc[questionId] = question.answer || "";
          } else if (question.type === "multiselect") {
            acc[questionId] = question.answer || [];
          }
        });
        return acc;
      }, {}),
      responsible: Array.isArray(editableData?.responsible)
        ? editableData.responsible.join(", ")
        : editableData?.responsible || "",
      accountable: editableData?.accountable || "",
      consulted: editableData?.consulted || "",
      informed: editableData?.informed || "",
      status: editableData?.status || "Open",
      // summary: editableData?.summary || "",
      type: normalizedType,
      date: editableData?.date ? new Date(editableData.date) : null,
      private:
        typeof editableData?.private === "boolean" ? editableData.private : false,
    };
  }, [editableData]);

  if (!audit && hasLoadedAudits) {
    return (
      <MainLayout>
        <div className={styles.notFound}>
          <div className={styles.emptyState}>We could not find this audit.</div>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate(`/${orgId}/audits${location.search}`)}
          >
            <ArrowLeft size={18} />
            Back to Audits
          </button>
        </div>
      </MainLayout>
    );
  }

  if (!audit) {
    return (
      <MainLayout>
        <PageLoader text="Loading audit..." pageLoader />
      </MainLayout>
    );
  }

  const modifyDescription = (description) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(description, "text/html");
    const links = doc.querySelectorAll("a");

    links.forEach((link) => {
      link.setAttribute("target", "_blank");
    });

    return doc.body.innerHTML;
  };

  const saveAuditChanges = async (values) => {
    if (!editableData?.id) return;

    setIsSaving(true);
    let hasChanges = false;

    const changedFields = { id: editableData.id };
    let hasAnyResponseChanged = false;
    const finalResponses = { ...editableData.responses };

    for (const formItem of editableData.form || []) {
      const questions = formItem.questions || {};

      for (const questionId in questions) {
        const question = questions[questionId];
        if (!question) continue;

        const existingAnswer =
          question.answer ||
          (question.type === "text" || question.type === "select" ? "" : []);
        const newResponse = values.responses?.[questionId];

        let hasChanged = false;

        if (question.type === "text") {
          if (typeof newResponse === "string" && newResponse.trim() !== "") {
            finalResponses[questionId] = newResponse;
            if (newResponse !== existingAnswer) {
              hasChanged = true;
            }
          } else if (existingAnswer) {
            finalResponses[questionId] = existingAnswer;
          }
        } else if (question.type === "file") {
          if (Array.isArray(newResponse) && newResponse.length > 0) {
            const existingArray = Array.isArray(existingAnswer)
              ? existingAnswer
              : [];

            const combinedFiles = [
              ...existingArray.map((answer) => answer.path || answer),
              ...newResponse,
            ];

            finalResponses[questionId] = combinedFiles;

            const existingPaths = existingArray.map((a) => a.path || a);
            const combinedPaths = combinedFiles.map((f) => f.path || f);

            if (JSON.stringify(existingPaths) !== JSON.stringify(combinedPaths)) {
              hasChanged = true;
            }
          } else if (Array.isArray(existingAnswer) && existingAnswer.length > 0) {
            finalResponses[questionId] = existingAnswer.map(
              (answer) => answer.path || answer,
            );
          } else {
            finalResponses[questionId] = [];
          }
        } else if (question.type === "select") {
          if (newResponse && newResponse.trim() !== "") {
            finalResponses[questionId] = newResponse;
            if (newResponse !== existingAnswer) {
              hasChanged = true;
            }
          } else if (existingAnswer) {
            finalResponses[questionId] = existingAnswer;
          }
        } else if (question.type === "multiselect") {
          if (Array.isArray(newResponse) && newResponse.length > 0) {
            const processedResponse = newResponse.map((item) => {
              return typeof item === "object" ? item.value || item.id : item;
            });

            finalResponses[questionId] = processedResponse;

            const existingArray = Array.isArray(existingAnswer)
              ? existingAnswer
              : [];

            if (
              JSON.stringify(processedResponse) !==
              JSON.stringify(existingArray)
            ) {
              hasChanged = true;
            }
          } else if (Array.isArray(existingAnswer) && existingAnswer.length > 0) {
            finalResponses[questionId] = existingAnswer;
          } else {
            finalResponses[questionId] = [];
          }
        } else {
          if (Array.isArray(newResponse)) {
            const nonNullAnswers = newResponse
              .filter((answer) => answer && answer !== null)
              .map((answer) => {
                return typeof answer === "object" && answer.path
                  ? answer.path
                  : answer;
              });

            const existingArray = Array.isArray(existingAnswer)
              ? existingAnswer
              : [];

            if (nonNullAnswers.length > 0 || existingArray.length > 0) {
              const combinedAnswers = [
                ...existingArray.map((answer) => answer.path || answer),
                ...nonNullAnswers,
              ];

              finalResponses[questionId] = combinedAnswers;

              const existingPaths = existingArray.map((a) => a.path || a);
              if (
                JSON.stringify(existingPaths) !==
                JSON.stringify(combinedAnswers)
              ) {
                hasChanged = true;
              }
            }
          } else if (Array.isArray(existingAnswer) && existingAnswer.length > 0) {
            finalResponses[questionId] = existingAnswer.map(
              (answer) => answer.path || answer,
            );
          } else {
            finalResponses[questionId] = [];
          }
        }

        if (hasChanged) {
          hasAnyResponseChanged = true;
        }
      }
    }

    const selectedType = values.type?.toLowerCase();
    if (selectedType && editableData.type !== selectedType) {
      changedFields.type = selectedType;
      hasChanges = true;
    }

    if (hasAnyResponseChanged) {
      changedFields.responses = finalResponses;
      hasChanges = true;
    }

    // if (values.summary && editableData.summary !== values.summary) {
    //   changedFields.summary = values.summary;
    //   hasChanges = true;
    // }

    if (values.date && editableData.date !== values.date) {
      const formattedDate =
        values.date instanceof Date
          ? values.date.toISOString().split("T")[0]
          : values.date;

      if (editableData.date !== formattedDate) {
        changedFields.date = formattedDate;
        hasChanges = true;
      }
    }

    const responsibleString = Array.isArray(values.responsible)
      ? values.responsible.join(", ")
      : values.responsible;

    if (responsibleString && editableData.responsible !== responsibleString) {
      changedFields.responsible = responsibleString;
      hasChanges = true;
    }

    ["accountable", "consulted", "informed"].forEach((field) => {
      const newValue = Array.isArray(values[field])
        ? values[field]
        : values[field]?.split(",").map((item) => item.trim()) || [];

      if (
        JSON.stringify(editableData[field]) !== JSON.stringify(newValue) &&
        !(newValue.length === 1 && newValue[0] === "")
      ) {
        changedFields[field] = newValue;
        hasChanges = true;
      }
    });

    if (values.status && editableData.status !== values.status) {
      changedFields.status = values.status;
      hasChanges = true;
    }

    if (
      typeof values.private === "boolean" &&
      editableData.private !== values.private
    ) {
      changedFields.private = values.private;
      hasChanges = true;
    }

    if (!hasChanges) {
      setIsSaving(false);
      return;
    }

    try {
      const payload = [changedFields];

      await axiosInstance.patch(
        `/nocodb/audit_tasks?org_id=${orgId}`,
        payload,
        {
          headers: {
            "session-token": sessionToken,
            "Content-Type": "application/json",
          },
        },
      );

      await dispatch(
        getOrganizationData(
          orgId,
          sessionToken,
          navigate,
          false,
          false,
          false,
          true,
          false,
          false,
        ),
      );
    } catch (error) {
      console.error("Error saving audit", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateSubmit = async (
    values,
    { resetForm, setFieldValue, setSubmitting },
  ) => {
    try {
      const requestBody = {
        body: values.body,
        attachments: (values.attachments || []).map((attachment) => attachment),
        audit_task: audit?.id || 0,
        finding: 0,
        improvement: 0,
      };

      const response = await axiosInstance.post(
        `/nocodb/update?org_id=${orgId}`,
        requestBody,
        {
          headers: {
            "session-token": sessionToken,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.status === 200) {
        const transformedAttachments = normalizeUpdateAttachments(
          values.attachments || [],
        );
        setUpdates((prevUpdates) => [
          {
            author: userEmail,
            date: new Date().toISOString(),
            body: values.body,
            attachments: transformedAttachments,
          },
          ...prevUpdates,
        ]);
        setFieldValue("attachments", []);

        await dispatch(
          getOrganizationData(
            orgId,
            sessionToken,
            null,
            false,
            false,
            false,
            true,
            false,
            false,
          ),
        );

        resetForm();
      }
    } catch (error) {
      console.error("Error adding update:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportWord = async () => {
    if (!audit || isExporting) return;
    setIsExporting(true);
    await handleExport({
      selectedItems: [audit],
      orgId,
      sessionToken,
      getKey: (item) => item.key,
      filePrefix: "Audit",
    });
    setIsExporting(false);
  };

  const handleOpenHsPopup = () => {
    dispatch(setHsRootLoading(true));
    setHsPopupOpen(true);
    dispatch(fetchHsRoot());
  };

  const renderTabContent = (tab, values, setFieldValue, onNewFinding) => {
    if (tab === "questions") {
      const formItems = editableData.form || [];
      if (!formItems.length) {
        return <div className={styles.emptyState}>No questions added yet.</div>;
      }

      return (
        <div className={styles.questionsPanel}>
          <Button
            text="Show HS codes"
            type="button"
            onClick={handleOpenHsPopup}
            className={styles.hsCodesButton}
          />
          {formItems.map((formItem, index) => {
            const questions = formItem.questions || {};
            const questionIds = Object.keys(questions);
            const risk = formItem.risk || {};
            const riskKey =
              risk.risk_id || risk.risk_num || risk.id || `Risk ${index + 1}`;
            const riskDescription =
              risk.qms_description || risk.description || "";
            const questionCount = questionIds.length;

            return (
              <div key={`${riskKey}-${index}`} className={styles.auditFormCard}>
                <div className={styles.auditFormHeader}>
                  <div className={styles.auditFormInfo}>
                    <div className={styles.auditFormTitle}>{riskKey}</div>
                    {riskDescription && (
                      <div className={styles.auditFormSubtitle}>
                        {riskDescription}
                      </div>
                    )}
                  </div>
                  <div className={styles.auditFormMeta}>
                    {questionCount} question{questionCount === 1 ? "" : "s"}
                  </div>
                </div>

                <QuestionAnswerList
                  items={questionIds.map((questionId) => {
                    const question = questions[questionId];
                    const responseValue = values.responses?.[questionId];
                    const hasResponseValue = Object.prototype.hasOwnProperty.call(
                      values.responses || {},
                      questionId,
                    );
                    const currentAnswers = hasResponseValue
                      ? responseValue
                      : question?.answer || [];

                    return {
                      question: question?.question || "Untitled question",
                      answer: {
                        type: question?.type,
                        answers: currentAnswers,
                        questionId,
                        modifyDescription,
                        editable: values.status !== "Done",
                        options: question?.options,
                      },
                    };
                  })}
                  setFieldValue={setFieldValue}
                  handleOpenNewFindingModal={onNewFinding}
                  loadingQuestionId={addingQuestionId}
                />
              </div>
            );
          })}
        </div>
      );
    }

    if (tab === "comments") {
      const commentEntries = updates
        .filter(isCommentUpdate)
        .map((update) => {
          const attachments = normalizeUpdateAttachments(
            update?.attachments || [],
          );
          return {
            date: getUpdateTimestamp(update),
            author: getUpdateAuthor(update),
            body: getUpdateBodyText(update).trim(),
            attachments,
          };
        })
        .filter((entry) => entry.body || entry.attachments.length)
        .sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateB - dateA;
        });

      if (!commentEntries.length) {
        return <div className={styles.emptyState}>No comments yet.</div>;
      }

      return (
        <div className={styles.commentsCard}>
          <div className={styles.commentsHeader}>
            <div className={styles.commentsTitleRow}>
              <MessageSquare size={20} />
              <div className={styles.commentsTitle}>Comments</div>
            </div>
            <div className={styles.commentsSubtitle}>
              Notes and attachments added by the team
            </div>
          </div>
          <div className={styles.commentsList}>
            {commentEntries.map((entry, idx) => {
              const relative = entry.date ? getRelativeTime(entry.date) : "";
              const timestamp = entry.date ? formatDateTime(entry.date) : "";
              const author = entry.author || "Unknown";

              return (
                <div key={`${author}-${entry.date || idx}`} className={styles.commentItem}>
                  <div className={styles.commentMetaRow}>
                    <div className={styles.commentAuthor}>{author}</div>
                    {relative && <div className={styles.commentTime}>{relative}</div>}
                  </div>
                  {timestamp && (
                    <div className={styles.commentTimestamp}>{timestamp}</div>
                  )}
                  {entry.body && (
                    <div className={styles.commentBody}>{entry.body}</div>
                  )}
                  {entry.attachments.length > 0 && (
                    <div className={styles.commentAttachments}>
                      {entry.attachments.map((attachment, attachmentIndex) => (
                        <a
                          key={`${attachment.url}-${attachmentIndex}`}
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.commentAttachment}
                        >
                          <Paperclip size={14} />
                          <span>{attachment.filename}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (tab === "change-history") {
      const historyEntries = updates
        .filter(isStructuredUpdate)
        .map((update) => ({
          type: "Updated",
          date: getUpdateTimestamp(update),
          author: getUpdateAuthor(update),
          body: getUpdateBodyText(update) || "Updated record",
        }));

      const sortedEntries = historyEntries
        .filter((entry) => entry.date || entry.author || entry.body)
        .sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateB - dateA;
        });

      if (!sortedEntries.length) {
        return <div className={styles.emptyState}>No changes yet.</div>;
      }
      return (
        <div className={styles.changeHistoryCard}>
          <div className={styles.changeHistoryHeader}>
            <div className={styles.changeHistoryTitleRow}>
              <Clock size={20} />
              <div className={styles.changeHistoryTitle}>Change History</div>
            </div>
            <div className={styles.changeHistorySubtitle}>
              Track of all changes made to this record
            </div>
          </div>
          <div className={styles.changeHistoryList}>
            {sortedEntries.map((entry, idx) => {
              const relative = entry.date ? getRelativeTime(entry.date) : "";
              const timestamp = entry.date ? formatDateTime(entry.date) : "";
              const rawBody =
                entry.body && typeof entry.body !== "string"
                  ? String(entry.body)
                  : entry.body || "";
              const parsedBody = rawBody ? parseUpdateBody(rawBody) : [];
              const hasStructuredBody =
                parsedBody.length > 1 || parsedBody.some((item) => item.label);
              return (
                <div
                  key={`${entry.type}-${idx}`}
                  className={classNames(
                    styles.changeHistoryItem,
                    styles.historyUpdated,
                  )}
                >
                  <div className={styles.changeHistoryMetaRow}>
                    <div className={styles.changeHistoryMetaLeft}>
                      <div
                        className={classNames(
                          styles.changeHistoryBadge,
                          styles.badgeUpdated,
                        )}
                      >
                        {entry.type}
                      </div>
                      {relative && (
                        <span className={styles.changeHistoryRelative}>
                          {relative}
                        </span>
                      )}
                    </div>
                    {timestamp && (
                      <span className={styles.changeHistoryTimestamp}>
                        {timestamp}
                      </span>
                    )}
                  </div>
                  {(entry.author || entry.body) && (
                    <div className={styles.changeHistoryDetails}>
                      {entry.author && (
                        <div className={styles.changeHistoryAuthor}>
                          <User size={14} />
                          <span>{entry.author}</span>
                        </div>
                      )}
                      {entry.body && (
                        <div className={styles.changeHistoryBody}>
                          {!hasStructuredBody ? (
                            <span className={styles.changeHistoryBodyText}>
                              {rawBody}
                            </span>
                          ) : (
                            <ul className={styles.changeHistoryBodyList}>
                              {parsedBody.map((item, itemIndex) => (
                                <li
                                  key={`${entry.type}-${idx}-${itemIndex}`}
                                  className={styles.changeHistoryBodyItem}
                                >
                                  {item.label ? (
                                    <>
                                      <span
                                        className={styles.changeHistoryBodyLabel}
                                      >
                                        {item.label}
                                      </span>
                                      <span
                                        className={styles.changeHistoryBodyValue}
                                      >
                                        {item.value || "Not set"}
                                      </span>
                                    </>
                                  ) : (
                                    <span
                                      className={styles.changeHistoryBodyText}
                                    >
                                      {item.text}
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div className={styles.detailsGrid}>
        <div className={styles.fieldBlock}>
          <label htmlFor="name">Audit name</label>
          <div className={styles.readOnlyValue}>{values.name || "Not set"}</div>
        </div>

        <div className={styles.fieldBlock}>
          <label htmlFor="date">Due date</label>
          <Field name="date" component={DatePickerField} />
        </div>

        <div className={styles.fieldBlock}>
          <label htmlFor="type">Audit type</label>
          <Field
            name="type"
            component={Select}
            options={typeOptions}
            text="Select type"
          />
        </div>

        <div className={styles.fieldBlock}>
          <label htmlFor="private">Privacy</label>
          <IconSelect
            name="private"
            options={privacyOptions}
            placeholder="Select privacy"
            variant="modal"
          />
        </div>

        {/*
        {values.status === "Done" ? (
          <div className={classNames(styles.fieldBlock, styles.fullWidth)}>
            <label htmlFor="summary">Summary</label>
            <Field
              name="summary"
              component={WYSIWYGEditor}
              placeholder="Add audit summary..."
            />
          </div>
        ) : (
          <div className={styles.summaryHint}>
            Summary becomes available once the audit is marked as Done.
          </div>
        )}
        */}
      </div>
    );
  };

  return (
    <MainLayout>
      <div className={styles.page}>
        <div className={styles.topBar}>
          <Button
            type="button"
            className={styles.backButton}
            onClick={() => navigate(`/${orgId}/audits${location.search}`)}
            text="Back to Audits"
            icon={<ArrowLeft size={16} />}
            iconPosition="left"
          />

          <div className={styles.actions}>
            <Button
              text={isExporting ? "Exporting..." : "Word"}
              className={styles.secondaryAction}
              icon={<FileText size={16} />}
              iconPosition="left"
              onClick={handleExportWord}
              disabled={isExporting}
            />
            <Button
              text={isSaving ? "Saving..." : "Save Changes"}
              className={styles.primaryAction}
              onClick={() => formikRef.current?.submitForm()}
              disabled={isSubmitting || isSaving || !isDirty}
              icon={<Save size={16} />}
              iconPosition="left"
            />
          </div>
        </div>

        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          enableReinitialize
          onSubmit={async (values, helpers) => {
            await saveAuditChanges(values);
            helpers.setSubmitting(false);
          }}
        >
          {({ values, dirty, isSubmitting: submitting, setFieldValue }) => {
            const statusTone =
              statusOptions.find((option) => option.id === values.status)?.tone ||
              "open";

            const handleOpenNewFindingModal = async (questionId) => {
              setAddingQuestionId(questionId);
              await saveAuditChanges(values);

              const formEntry = (editableData.form || []).find((formItem) =>
                Object.keys(formItem.questions || {}).includes(questionId),
              );

              if (formEntry) {
                dispatch(
                  showModal({
                    name: "new_finding_modal",
                    data: {
                      ...editableData,
                      questionId,
                      risk: formEntry.risk,
                      skipAuditModalReopen: true,
                    },
                  }),
                );
              }

              setAddingQuestionId(null);
            };

            return (
              <Form className={styles.form}>
                <FormStateBridge
                  dirty={dirty}
                  isSubmitting={submitting}
                  onChange={handleFormState}
                />
                <div className={styles.headerCard}>
                  <div className={styles.headerTop}>
                    <div className={styles.keyPill}>{audit.key}</div>
                    <div className={styles.statusBlock}>
                      <div className={styles.statusLabel}>Status</div>
                      <OutsideClickHandler
                        onOutsideClick={() => setIsStatusOpen(false)}
                        disabled={!isStatusOpen}
                      >
                        <div className={styles.statusDropdown}>
                          <button
                            type="button"
                            className={classNames(
                              styles.statusSelect,
                              styles[`status-${statusTone}`],
                            )}
                            onClick={() => setIsStatusOpen((prev) => !prev)}
                          >
                            <span>{values.status}</span>
                            <ChevronDown size={16} />
                          </button>
                          {isStatusOpen && (
                            <div className={styles.statusMenu}>
                              {statusOptions.map((option) => (
                                <button
                                  key={option.id}
                                  type="button"
                                  className={classNames(styles.statusOption, {
                                    [styles.activeStatus]:
                                      values.status === option.id,
                                  })}
                                  onClick={() => {
                                    formikRef.current?.setFieldValue(
                                      "status",
                                      option.id,
                                    );
                                    setIsStatusOpen(false);
                                  }}
                                >
                                  <span className={styles.statusCheckSlot}>
                                    {values.status === option.id && (
                                      <Check size={14} />
                                    )}
                                  </span>
                                  <div
                                    className={classNames(
                                      styles.statusDot,
                                      styles[`dot-${option.tone}`],
                                    )}
                                  />
                                  <span className={styles.statusOptionLabel}>
                                    {option.label}
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </OutsideClickHandler>
                    </div>
                  </div>
                </div>

                <div className={styles.bodyGrid}>
                  <div className={styles.raciSection}>
                    <div className={styles.sectionLabel}>RACI assignment</div>
                    <RaciAssignment />
                  </div>

                  <div className={styles.mainColumn}>
                    <div className={styles.tabHeaderRow}>
                      <div className={styles.tabs}>
                        <button
                          type="button"
                          className={classNames(styles.tabButton, {
                            [styles.activeTab]: activeTab === "details",
                          })}
                          onClick={() => setActiveTab("details")}
                        >
                          Details
                        </button>
                        <button
                          type="button"
                          className={classNames(styles.tabButton, {
                            [styles.activeTab]: activeTab === "questions",
                          })}
                          onClick={() => setActiveTab("questions")}
                        >
                          Questions & Answers
                        </button>
                        <button
                          type="button"
                          className={classNames(styles.tabButton, {
                            [styles.activeTab]: activeTab === "change-history",
                          })}
                          onClick={() => setActiveTab("change-history")}
                        >
                          Change History
                        </button>
                        <button
                          type="button"
                          className={classNames(styles.tabButton, {
                            [styles.activeTab]: activeTab === "comments",
                          })}
                          onClick={() => setActiveTab("comments")}
                        >
                          Comments
                        </button>
                      </div>
                    </div>

                    <div className={styles.tabPanel}>
                      {renderTabContent(
                        activeTab,
                        values,
                        setFieldValue,
                        handleOpenNewFindingModal,
                      )}
                    </div>
                  </div>

                  <div className={styles.sideColumn}>
                    <div className={styles.communicationCard}>
                      <div className={styles.communicationHeader}>
                        <div>
                          <div className={styles.communicationTitle}>
                            Communication Module
                          </div>
                          <div className={styles.communicationSubtitle}>
                            Add comments for this audit.
                          </div>
                        </div>
                      </div>
                      <Formik
                        initialValues={{ body: "", attachments: [] }}
                        onSubmit={handleUpdateSubmit}
                      >
                        {({ values, isSubmitting, submitForm }) => (
                          <div className={styles.communicationForm}>
                            {userEmail && (
                              <div className={styles.communicationUser}>
                                {userEmail}
                              </div>
                            )}
                            <Field
                              name="body"
                              component={Textarea}
                              minHeight="90px"
                              className={styles.communicationTextarea}
                              placeholder="Add a comment..."
                            />
                            <Field
                              name="attachments"
                              component={FormikUpload}
                              greenIcon
                              className={styles.communicationUpload}
                              labelClassName={styles.communicationUploadLabel}
                              labelContent={
                                <span className={styles.communicationUploadContent}>
                                  <Paperclip
                                    size={14}
                                    className={styles.communicationUploadIcon}
                                  />
                                  <span className={styles.communicationUploadText}>
                                    Attach files
                                  </span>
                                  <span className={styles.communicationUploadHint}>
                                    PDF, PNG, DOC
                                  </span>
                                </span>
                              }
                            />
                            <Button
                              text={isSubmitting ? "Posting..." : "Post comment"}
                              className={styles.communicationSubmit}
                              icon={<Send size={14} />}
                              iconPosition="right"
                              type="button"
                              onClick={submitForm}
                              disabled={
                                isSubmitting ||
                                (!values.body.trim() &&
                                  (values.attachments || []).length === 0)
                              }
                            />
                          </div>
                        )}
                      </Formik>
                    </div>
                    <div className={styles.sideCard}>
                      <div className={styles.sideCardHeader}>Linked Findings</div>
                      {linkedFindingEntries.length ? (
                        <>
                          <div className={styles.linkedFindingsList}>
                            {linkedFindingEntries.map(({ key, data }) => {
                              const status = data?.status;
                              const description =
                                data?.description || data?.reference || "No description";
                              return (
                                <button
                                  key={key}
                                  type="button"
                                  className={styles.linkedFindingButton}
                                  onClick={() =>
                                    navigate(
                                      `/${orgId}/findings/${key}${location.search}`,
                                    )
                                  }
                                >
                                  <div className={styles.linkedFindingHeader}>
                                    <div className={styles.linkedFindingKey}>{key}</div>
                                    {status && (
                                      <span
                                        className={classNames(
                                          "statusCell",
                                          status === "Open"
                                            ? "openStatus"
                                            : status === "In Progress"
                                              ? "inProgressStatus"
                                              : status === "In Review"
                                                ? "inReviewStatus"
                                                : "resolvedStatus",
                                        )}
                                      >
                                        {status}
                                      </span>
                                    )}
                                  </div>
                                  <div className={styles.linkedFindingDescription}>
                                    {description}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                          <div className={styles.linkedFindingsCount}>
                            {linkedFindingEntries.length} finding
                            {linkedFindingEntries.length === 1 ? "" : "s"} linked
                          </div>
                        </>
                      ) : (
                        <div className={styles.emptyState}>No linked findings yet.</div>
                      )}
                    </div>
                    {showMetaCard && (
                      <div className={classNames(styles.sideCard, styles.metaCard)}>
                        <div className={styles.metaGrid}>
                          <div>
                            <div className={styles.metaLabel}>Last updated</div>
                            <div className={styles.metaValue}>
                              {lastUpdatedAt ? formatDateTime(lastUpdatedAt) : "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
      <HsCodesPopup
        open={hsPopupOpen}
        root={root}
        expandedNodes={expandedNodes}
        rootLoading={rootLoading}
        onClose={() => {
          setHsPopupOpen(false);
          dispatch(clearHsCodes());
        }}
        onExpand={(code) => dispatch(fetchHsChildren(code))}
      />
    </MainLayout>
  );
};

export default AuditDetails;
